<template>
	<DrawerPanel
		ref="quizdrawer"
		title=""
		bottom="-95px"
		:blurBackground="true"
		:closeBackground="false"
		:openHeight="dynamicHeight"
		closedHeight="0px"
		:startOpened="false"
		:safeAreaPadding="currentPage !== 1"
		:back="currentPage !== 3 && currentPage !== 1 && currentPage !== 4"
		:close="currentPage !== 3  && currentPage !== 4"
		:divider="false"
		backgroundColor="#FFFFFF"
		openBackgroundColor="#F9F7F3;"
		transitionDuration=".5s"
        @goBack="goBack"
		@opened="$emit('drawer-opened')"
		@closed="$emit('drawer-closed')"
	>
		<!-- Page 1: Quiz Info -->
		<Transition name="page-fade" mode="out-in">
		<div v-if="currentPage === 1" class="quiz-intro-drawer" key="page1">
			<div class="quiz-intro-header">
				<div class="quiz-intro-content">
					<h2 class="quiz-intro-title">{{ heading }}</h2>
					<p class="quiz-intro-subtitle">{{ headingDescription }}</p>
				</div>
				
				<div class="quiz-intro-image">
					<img :src="currentQuestionBg" />
				</div>
			</div>
			
			<div class="quiz-intro-footer">
			<div class="quiz-intro-text">
				<span class="quiz-day">Day {{ currentDay }} of {{ totalDays }}</span>
				<p class="preamble" v-html="preamble"></p>
			</div>
			<div class="quiz-intro-actions">
				<MGButton text="Play now" :fullWidth="false" :arrow="true" :noCaps="true" @click="goToQuestion"/>
				<a href="#" class="quiz-terms-link" @click.prevent="$emit('terms-click')">Terms & Conditions</a>
			</div>
		</div>
		</div>

		<!-- Page 2: Quiz Question -->
		<div v-else-if="currentPage === 2" class="quiz-question-drawer" key="page2">
			<div class="quiz-question-header">				
				<div class="quiz-day-indicator">
					<div class="day-badge">
						<span class="day-number">{{ currentDay }}</span>
						<img :src="peelLarge" class="peel-corner" />
					</div>
				</div>

				<div class="quiz-question-content">
					<h2 class="question-text">{{ questionText }}</h2>
				</div>
			</div>

			<div class="quiz-question-footer" :class="{ 'has-selection': selectedAnswer !== null }">
				<div class="answer-options" :class="{ 'small-text': hasLongAnswers }">
					<div 
						v-for="(answer, index) in possibleAnswers" 
						:key="index"
						class="answer-option"
						:class="{ 'selected': selectedAnswer === answer }"
						@click="selectAnswer(answer)"
					>
						<span class="answer-letter">{{ String.fromCharCode(65 + index) }}</span>
						<span class="answer-text" :class="{ 'answer-text-selected': selectedAnswer === answer }">{{ answer.value }}</span>
						<img v-if="selectedAnswer === answer" :src="arrowRight" class="answer-arrow" @click.stop="submitAnswer(selectedAnswer)" />
					</div>
				</div>

				<MGButton v-if="selectedAnswer !== null" text="Submit" :fullWidth="false" :arrow="true" :disabled="selectedAnswer === null" @click="submitAnswer(selectedAnswer)" />
			</div>
		</div>

		<!-- Page 3: Answer Result Loading -->
		<div v-else-if="currentPage === 3 && !answerResponse" class="quiz-result-loading" key="page3-loading">
			<div class="loading-spinner"></div>
			<p class="loading-text">Checking your answer...</p>
		</div>

		<!-- Page 3: Answer Result -->
		<div v-else-if="currentPage === 3 && answerResponse" class="quiz-result-drawer" key="page3">
			<div class="quiz-result-header">
				<div class="result-status">
					<img v-if="answerResponse.isCorrect" :src="tickUrl" width="45" height="40"/>
					<img v-else :src="crossUrl" width="40" height="40"/>
					<h2 class="result-title">{{ answerResponse.isCorrect ? 'Correct!' : 'Oops, not quite!' }}</h2>
				</div>
				
				<div v-if="percentageWrong" class="result-stat">
					<span>{{ percentageWrong }}% of players got that wrong!</span>
				</div>
			</div>

			<div class="quiz-result-content">
				<div class="correct-answer-card">
					<div class="answer-indicator">
						<span class="answer-letter-result">{{ correctAnswerLetter }}</span>
					</div>
					<span class="answer-text-result">{{ correctAnswer ? correctAnswer.value : '' }}</span>
				</div>

				<p class="explanation-text">{{ answerResponse.answerDescription }}</p>
                <div v-if="answerResponse.answerExample" class="example-text">
                    <p>{{ answerResponse.answerExample }}</p>
                </div>

			</div>

			<div class="quiz-result-footer" @click.stop>
				<div class="chat-button-wrapper">
					<ChatMgButton :hover="false" :prompt="questionText" :initialPrompt="true" :size="64" />
				</div>
				<p class="centered">More questions?  Ask our Financial Assistant</p>	
				<MGButton text="Continue" @click="onGoToStats" :fullWidth="false" :arrow="true" />
				<a href="#" class="quiz-terms-link" @click.prevent="$emit('terms-click')">Terms & Conditions</a>
			</div>
		</div>

		<!-- Page 4: Stats -->
		<div v-else-if="currentPage === 4" class="quiz-stats-drawer" key="page4">
			<!-- 'Snowing' style effect for final question -->
			<QuizFinalDayFallingEffect v-if="isFinalQuestion && quiz.finalDaySpecialImageUrl !== null" :imageSrc="quiz.finalDaySpecialImageUrl" />
			
			<div 
				class="stats-day-badge" 
				:class="{ 'peel-active': peelAnimate, 'clickable': animationsComplete && !openToStats }" 
				:style="statsBackgroundStyle"
				@click="!openToStats && animationsComplete && replayAnimations()"
			>
				<span v-if="!openToStats" class="stats-day-number" :style="{ textShadow: '0 0 8px rgba(0,0,0,0.3), 0 0 4px rgba(0,0,0,0.2)' }">{{ currentDay || '?' }}</span>
				<img v-if="!openToStats" :src="peelLarge" class="peel-corner-stats" />
			</div>

			<div class="stats-cards">
				<div class="stat-card-group">
					<div class="stat-card" :class="{ 'has-value': card1Animate }">
						<div class="stat-header">
							<span class="stat-title">Total<br>Answered</span>
						</div>
						<div class="stat-number">
							<span v-if="showTotalAnsweredBonus" class="bonus-animation">+1</span>
							<span v-else class="stat-value">{{ displayTotalAnswered }}</span>
						</div>
					</div>
					<div class="stat-card" :class="{ 'has-value': card1Animate }">
						<div class="stat-header">
							<span class="stat-title">Total<br>Correct</span>
						</div>
						<div class="stat-number">
							<span v-if="showTotalAnsweredBonus && isCorrect" class="bonus-animation">+1</span>
							<span v-else class="stat-value">{{ displayTotalCorrect }}</span>
						</div>
					</div>
				</div>

				<div class="stat-card stat-card-streak" :class="{ 'has-value': card2Animate }">
					<div class="stat-header-streak">
						<span class="stat-title">Play streak count</span>
						<div class="streak-count">
							<span class="streak-current">{{ card2Animate ? displayStreakAfter : displayStreakBefore }}</span>
							<span class="streak-total">/{{ totalDays }}</span>
						</div>
					</div>
					<div class="streak-milestones" :class="{ 'streak-milestones-empty': (card2Animate ? displayStreakAfter : displayStreakBefore) === 0 }">
						<template v-for="(milestone, index) in milestoneDays" :key="milestone.day">
							<div v-if="milestone.isMilestone && !milestone.isFirst" class="milestone-marker">
								<img v-if="hitMilestoneDay === milestone.day" :src="flameUrl" class="milestone-flame" :class="{ 'flame-disappear': flameDisappearing }" />
								<div 
									:class="milestone.isFilled ? 'milestone-circle' : 'milestone-empty-large'"
									:style="milestone.isFilled && card2Animate ? { animationDelay: (index * 0.08) + 's' } : {}"
									class="dot-fill-animate"
								></div>
								<span 
									v-if="milestone.label" 
									class="milestone-label" 
									:class="{ 'milestone-label-active': milestone.labelIsActive }"
								>
									{{ milestone.label }}
								</span>
							</div>
							<div v-else-if="!milestone.isMilestone" class="milestone-dot-wrapper">
								<div 
									:class="(card2Animate ? displayStreakAfter : displayStreakBefore) >= milestone.day - 1 ? 'milestone-dot-small' : 'milestone-dot-small-empty'"
									:style="(card2Animate ? displayStreakAfter : displayStreakBefore) >= milestone.day - 1 && card2Animate ? { animationDelay: (index * 0.08) + 's' } : {}"
									class="dot-fill-animate"
								></div>
							</div>
						</template>
					</div>
				</div>

				<div class="stat-card" :class="{ 'has-value': card3Animate }">
					<div class="stat-header">
						<span class="stat-title">Prize draw<br>entries</span>
					</div>
					<div class="stat-number">
						<span v-if="showPrizeBonus" class="bonus-animation">+{{ prizeBonus }}</span>
						<span v-else class="stat-value">{{ displayPrizeEntries }}</span>
					</div>
				</div>
			</div>
			<Transition>
			<div v-if="animationsComplete" class="stats-footer">
				<p class="stats-message">
					<template v-if="isFinalQuestion">
						<template v-if="totalCorrect === totalDays">
							Congratulations!<br>
							You have earned maximum entries to our prize draw. We will be contacting winners soon, good luck!
						</template>
						<template v-else>
							That's it!  We will be contacting winners soon, good luck!
						</template>
					</template>
					<template v-else>
						Remember, there are bonus entries everytime you +{{ milestoneInterval }} to your streak. Check back tomorrow to maximise your entries!
					</template>
				</p>
				
					<div class="quiz-notifications">
						<div class="quiz-notifications-toggle">
							<span class="toggle-label">Notifications:</span>
							<div class="toggle-control">
							<span class="toggle-text" :class="{ 'active': !notificationsEnabled }">Off</span>
							<div class="toggle-switch" :class="{ 'toggle-on': notificationsEnabled }" @click="toggleNotifications">
								<div class="toggle-knob"></div>
							</div>
							<span class="toggle-text" :class="{ 'active': notificationsEnabled }">On</span>
						</div>
					</div>
				</div>
				
				<MGButton text="Back to home" :arrow="true" :fullWidth="false" :noCaps="true" @click="onBackToHomeClick" />
				<a href="#" class="quiz-terms-link" @click.prevent="$emit('terms-click')">Terms & Conditions</a>
			</div>
		</Transition>
	</div>
		</Transition>
	</DrawerPanel>
</template>

<script>
import DrawerPanel from './DrawerPanel.vue';
import MGButton from './MGButton.vue';
import ChatMgButton from './ChatMgButton.vue';
import QuizFinalDayFallingEffect from './QuizFinalDayFallingEffect.vue';

export default {
	name: 'QuizDrawerPlayPanel',
    props: {
        openHeight: {
            type: String,
            default: '80%'
        },
		quiz: {
			type: Object,
			required: false
		},
		customer: {
			type: Object,
			required: false
		},
		originalQuizPoints: {
			type: Object,
			required: false
		},
		answerResponse: {
			type: Object,
			required: false
		},
		openToStats: {
			type: Boolean,
			default: false
		},
	},
	computed: {
		dynamicHeight() {
			return this.currentPage === 1 ? 'calc(90vh + 95px)' : 'calc(100vh + 95px)';
		},
		heading() {
			return this.quiz.tagLineHeader;
		},
		headingDescription() {
			var currentQuestion = this.quiz.questions.find(q => this.isQuestionCurrent(q));
			return currentQuestion ? currentQuestion.dailyDescription : '';
		},
		preamble() {
			var currentQuestion = this.quiz.questions.find(q => this.isQuestionCurrent(q));
			return currentQuestion ? currentQuestion.preamble : '';
		},
		currentDay() {
			return this.currentQuestion ? this.quiz.questions.indexOf(this.currentQuestion) + 1 : 0;
		},
		totalDays() {
			return this.quiz.questions.length;
		},
		isFinalQuestion() {
			// When on stats page (openToStats), use originalTotalAnswered instead of currentDay
			// because currentDay relies on finding an active question which may not exist
			const dayToCheck = this.openToStats ? this.originalTotalAnswered : this.currentDay;
			return dayToCheck === this.totalDays;
		},
		currentQuestion() {
			return this.quiz.questions.find(q => this.isQuestionCurrent(q));
		},
		// Safe asset URLs (guard require so tests don't throw if assets aren't resolvable)
		currentQuestionBg() {
			if (this.currentQuestion && this.currentQuestion.bgUrl) return this.currentQuestion.bgUrl;
			try {
				return require('@/assets/quiz/quiz-default-bg.png');
			} catch (e) {
				return '';
			}
		},
		defaultQuizBg() {
			try {
				return require('@/assets/quiz/quiz-default-bg.png');
			} catch (e) {
				return '';
			}
		},
		peelLarge() {
			try {
				return require('@/assets/quiz/peel-large.svg');
			} catch (e) {
				return '';
			}
		},
		arrowRight() {
			try {
				return require('@/assets/arrow-right.png');
			} catch (e) {
				return '';
			}
		},
		tickUrl() {
			try {
				return require('@/assets/quiz/tick.png');
			} catch (e) {
				return '';
			}
		},
		crossUrl() {
			try {
				return require('@/assets/quiz/cross.png');
			} catch (e) {
				return '';
			}
		},
		flameUrl() {
			try {
				return require('@/assets/quiz/flame.png');
			} catch (e) {
				return '';
			}
		},
		statsBackgroundStyle() {
			const defaultBg = this.defaultQuizBg || '';
			const bg = this.openToStats ? (this.quiz?.bgUrl || defaultBg) : (this.currentQuestion?.thumbUrl || defaultBg);
			return { backgroundImage: bg ? `url(${bg})` : '' };
		},
		questionText() {
			return this.currentQuestion ? this.currentQuestion.value : '';
		},
		possibleAnswers() {
			return this.currentQuestion ? this.currentQuestion.answers : [];
		},
		hasLongAnswers() {
			// Check if any answer text is long enough to wrap to multiple lines
			// Roughly 40 characters would wrap on a typical mobile screen
			return this.possibleAnswers.some(answer => answer.value && answer.value.length > 40);
		},
		percentageWrong() {
			if (this.answerResponse.percentageCorrectCustomers == 0) {
				return null;
			}
			return this.answerResponse ? Math.round(100 - this.answerResponse.percentageCorrectCustomers) : null;
		},
		selectedAnswerLetter() {
			if (this.selectedAnswer) {
				const index = this.possibleAnswers.indexOf(this.selectedAnswer);
				return String.fromCharCode(65 + index);
			}
			return '';
		},
		correctAnswer() {
			if (!this.currentQuestion || !this.currentQuestion.answers) {
				return null;
			}
			return this.currentQuestion.answers.find(answer => answer.isCorrect);
		},
		correctAnswerLetter() {
			if (this.correctAnswer) {
				const index = this.possibleAnswers.indexOf(this.correctAnswer);
				return String.fromCharCode(65 + index);
			}
			return '';
		},
		explanation() {
			return this.currentQuestion ? this.currentQuestion.answerDescription : '';
		},
		example() {
			return this.currentQuestion ? this.currentQuestion.example : '';
		},
		milestoneInterval() {
			return this.originalQuizPoints?.streakMeterInterval || 5;
		},
		totalAnswered() {
			return this.answerResponse?.points.answered || 0;
		},
		totalCorrect() {
			return this.answerResponse?.points.correct || 0;
		},
		originalTotalAnswered() {
			return this.originalQuizPoints?.answered || 0;
		},
		originalTotalCorrect() {
			return this.originalQuizPoints?.correct || 0;
		},
		currentStreak() {
			return (this.originalQuizPoints?.streakMeter || 0);
		},
		prizeEntries() {
			return this.answerResponse?.points.total || 0;
		},
		milestoneDays() {
			// Determine which streak value to show based on animation state
			// Before animation: show displayStreakBefore (original streak)
			// During/after animation: show displayStreakAfter (new streak after answering)
			const streakToShow = this.card2Animate ? this.displayStreakAfter : this.displayStreakBefore;
			
			// Create an array for each day in the quiz (plus day 1 as starting point)
			// totalDays + 1 because we include day 1 (the starting milestone)
			const result = Array.from({ length: this.totalDays + 1 }, (_, i) => {
				const day = i + 1; // Convert 0-based index to 1-based day number
				
				// Determine if this day is a milestone
				// Day 1 is always a milestone (starting point)
				// Other milestones occur every 'milestoneInterval' days (e.g., every 5 days: 6, 11, 16, 21, etc.)
				const isMilestone = day === 1 || (day - 1) % this.milestoneInterval === 0;
				
				// Determine if this day's dot should be filled (user has reached this day)
				// Day 1 is filled if streak > 0
				// Other days are filled if the streak count >= (day - 1)
				// Example: if streak is 3, then days 1, 2, 3, 4 should be filled (day-1: 0, 1, 2, 3)
				const isFilled = (day === 1 && streakToShow > 0) || (day > 1 && streakToShow >= day - 1);
				
				// Calculate the bonus label for milestone days
				let label = null;
				if (isMilestone && day > 1) {
					// Calculate which milestone number this is (1st, 2nd, 3rd, etc.)
					// Example: if milestoneInterval is 5, day 6 is milestone 1, day 11 is milestone 2
					const milestoneNumber = (day - 1) / this.milestoneInterval;
					
					// Determine the bonus points for this milestone
					// Example: day 6 gets +5 points, day 11 gets +10 points, final day gets total
					const bonus = milestoneNumber === Math.floor(this.totalDays / this.milestoneInterval) ? this.totalDays : (day - 1);
					label = `+${bonus}`;
				}

				return {
					day,                    // The day number (1-based)
					isMilestone,            // Whether this is a milestone day (larger circle)
					isFilled,               // Whether the dot should be filled/colored
					isFirst: day === 1,     // Whether this is the first day (special styling)
					label,                  // The bonus point label (e.g., "+5", "+10", "+25")
					labelIsActive: streakToShow >= day - 1,  // Whether to highlight the label
				};
			});
			return result;
		},
		notificationsEnabled: {
			get() {
				return this.customer?.preferences?.quizNotifications ?? true;
			},
			set(value) {
				// Emit to parent to handle saving
				this.$emit('notifications-change', value);
			}
		}
	},
	components: {
		DrawerPanel,
        MGButton,
        ChatMgButton,
        QuizFinalDayFallingEffect
	},
	emits: ['terms-click', 'continue', 'back-to-home', 'submit-answer', 'drawer-opened', 'drawer-closed', 'notifications-change'],
	data() {
		return {
			currentPage: 1,
			selectedAnswer: null,
			isCorrect: false,
			
			// Animation state
			displayTotalAnswered: 0,
			displayTotalCorrect: 0,
			displayStreakBefore: 0,
			displayStreakAfter: 0,
			displayPrizeEntries: 0,
			prizeBonus: 0,
			peelAnimate: false,
			card1Animate: false,
			card2Animate: false,
			card3Animate: false,
			showTotalAnsweredBonus: false,
			showStreakBonus: false,
			showPrizeBonus: false,
			hitMilestoneDay: null,
			isMilestoneHit: false,
			flameDisappearing: false,
			animationsComplete: false,
		}
	},
	methods: {
		isQuestionCurrent(question) {
			if (!question || !question.startDateTime || !question.endDateTime) {
				return false;
			}
			
			const now = new Date();
			const startDate = new Date(question.startDateTime);
			const endDate = new Date(question.endDateTime);

			return now >= startDate && now <= endDate;
		},
		openDrawer() {
			// Check if there's a current question available (unless opening to stats only)
			if (!this.openToStats && !this.currentQuestion) {
				return;
			}
			
			this.$refs.quizdrawer.openDrawer();
			
			// Open directly to stats page if requested
			if (this.openToStats) {
				this.currentPage = 4;
				this.isCorrect = false;
				// Set display values to current values (no animation)
				this.displayTotalAnswered = this.originalQuizPoints?.answered || 0;
				this.displayTotalCorrect = this.originalQuizPoints?.correct || 0;
				this.displayStreakBefore = this.originalQuizPoints?.streakMeter || 0;
				this.displayStreakAfter = this.originalQuizPoints?.streakMeter || 0;
				this.displayPrizeEntries = this.originalQuizPoints?.total || 0;
				this.prizeBonus = 0;
				this.card1Animate = true;
				this.card2Animate = true;
				this.card3Animate = true;
				this.animationsComplete = true;
			} else {
				this.currentPage = 1;
				this.selectedAnswer = null;
				
				// Reset animation state
				this.displayTotalAnswered = 0;
				this.displayTotalCorrect = 0;
				this.displayPrizeEntries = 0;
				this.prizeBonus = 0;
				this.peelAnimate = false;
				this.card1Animate = false;
				this.card2Animate = false;
				this.card3Animate = false;
				this.showTotalAnsweredBonus = false;
				this.showStreakBonus = false;
				this.showPrizeBonus = false;
				this.hitMilestoneDay = null;
				this.isMilestoneHit = false;
				this.flameDisappearing = false;
				this.animationsComplete = false;
			}
		},
		closeDrawer() {
			this.$refs.quizdrawer.closeDrawer();
		},
		goToQuestion() {
			this.currentPage = 2;
		},
		goBack() {
			this.currentPage = 1;
			this.selectedAnswer = null;
		},
		selectAnswer(answer) {
			this.selectedAnswer = answer;
		},
		submitAnswer(selectedAnswer) {
			if (selectedAnswer !== null) {
				// Check if the current question has expired
				if (!this.isQuestionCurrent(this.currentQuestion)) {
					// Question has expired, close drawer and go back to home
					this.closeDrawer();
					this.$emit('back-to-home', false);
					return;
				}
				
				this.$emit('submit-answer', selectedAnswer);
				this.currentPage = 3;
			}
		},
        onGoToStats() {
			// Set isCorrect from answerResponse
			this.isCorrect = this.answerResponse?.isCorrect || false;
			
			// Initialize animation state
			this.resetAnimationState();
			
			this.currentPage = 4;
			this.runAnimations();
		},
        onBackToHomeClick() {
            this.$emit('back-to-home', this.isCorrect);
            this.closeDrawer();
        },
		toggleNotifications() {
			this.notificationsEnabled = !this.notificationsEnabled;
		},
		replayAnimations() {
			// Reset animation state
			this.resetAnimationState();
			
			// Pause 1 second before starting animations
			this.$nextTick(async () => {
				await this.delay(1000);
				this.runAnimations();
			});
		},
		// Shared method to initialize/reset animation state
		resetAnimationState() {
			// Initialize display values (before animation) - use original values directly
			this.displayTotalAnswered = this.originalTotalAnswered;
			this.displayTotalCorrect = this.originalTotalCorrect;
			this.displayStreakBefore = (this.originalQuizPoints?.streakMeter || 0);
			this.displayStreakAfter = this.answerResponse?.points.streakMeter || 0;

			// Calculate the actual difference between before and after totals
			const originalTotal = this.originalQuizPoints?.total || 0;
			const newTotal = this.answerResponse?.points.total || 0;
			this.prizeBonus = newTotal - originalTotal;
			
			// Start with original total (before answering)
			this.displayPrizeEntries = originalTotal;

			// Reset animation flags
			this.peelAnimate = false;
			this.card1Animate = false;
			this.card2Animate = false;
			this.card3Animate = false;
			this.showTotalAnsweredBonus = false;
			this.showStreakBonus = false;
			this.showPrizeBonus = false;
			this.hitMilestoneDay = null;
			this.flameDisappearing = false;
			this.animationsComplete = false;
			
			// Check if a milestone was hit - show flame whenever streak reaches a milestone
			const newStreak = this.answerResponse?.points.streakMeter || 0;
			const interval = parseInt(this.milestoneInterval, 10);
			this.isMilestoneHit = (newStreak > 0 && newStreak % interval === 0);
			console.log('Milestone check:', { 
				newStreak, 
				interval, 
				isCorrect: this.isCorrect, 
				isMilestoneHit: this.isMilestoneHit,
				calculation: newStreak % interval,
				willShowOnDay: newStreak + 1
			});
		},
		// Helper function for delays
		delay(ms) {
			return new Promise(resolve => setTimeout(resolve, ms));
		},
		async runAnimations() {
			// Initial delay before starting peel animation
			await this.delay(1000);
			this.peelAnimate = true;
			
			// Wait for peel animation to complete
			await this.delay(1000);
			
			// Panel 1: Total Answered animation
			this.card1Animate = true;
			this.showTotalAnsweredBonus = true;
			await this.delay(1000);
			this.showTotalAnsweredBonus = false;
			this.displayTotalAnswered = this.totalAnswered;
			this.displayTotalCorrect = this.totalCorrect;
			
			// Panel 2: Streak animation
			await this.delay(100);
			this.card2Animate = true;
			
			if (this.isCorrect) {
				this.showStreakBonus = true;
				await this.delay(500);
				this.showStreakBonus = false;
			}
			
			// Milestone flame animation if applicable (runs in background)
			// Show flame regardless of whether answer was correct or not
			if (this.isMilestoneHit) {
				// The flame should appear on the milestone day marker
				// If streak = 5, the milestone day is 6 (because day = streak + 1)
				const milestoneDayNumber = this.answerResponse?.points.streakMeter + 1 || 0;
				this.hitMilestoneDay = milestoneDayNumber;
				console.log('Showing flame on day:', milestoneDayNumber);
				console.log('Milestone days array:', this.milestoneDays.filter(m => m.isMilestone).map(m => ({ day: m.day, label: m.label })));
				// Start flame disappear after 2000ms, don't wait for it
				setTimeout(async () => {
					this.flameDisappearing = true;
					await this.delay(500);
					this.hitMilestoneDay = null;
					this.isMilestoneHit = false;
					this.flameDisappearing = false;
				}, 2000);
			} else {
				console.log('No milestone hit - isMilestoneHit is false');
			}
			
			// Panel 3: Prize Entries animation (starts while flame may still be visible)
			await this.delay(800);
			this.card3Animate = true;
			this.showPrizeBonus = true;
			await this.delay(1000);
			this.showPrizeBonus = false;
			this.displayPrizeEntries = this.prizeEntries;
			
			// All animations complete
			this.animationsComplete = true;
		}
	}
}
</script>

<style scoped>
/* ========================================
   COMMON/GLOBAL STYLES
   ======================================== */

/* Page transition animations - used across all pages */
.page-fade-enter-active,
.page-fade-leave-active {
	transition: opacity 0.3s ease;
}

.page-fade-enter-from,
.page-fade-leave-to {
	/* Start / end with transparent state only */
	opacity: 0;
}

/* Global component overrides */
:deep(.drawer) {
	z-index: 3000 !important;
}

:deep(.bg) {
	z-index: 2999 !important;
}

/* Shared elements used across multiple pages */
.quiz-terms-link {
	margin-top: 0;
	font-weight: bold;
	font-size: 13px;
	line-height: 1.231em;
	text-align: center;
	color: var(--background-color-primary);
	text-decoration: none;
	padding: 8px 16px;
	border-radius: 100px;
}

.quiz-day {
	font-weight: bold;
	font-size: 18px;
	line-height: 1.333em;
	text-align: center;
	margin: 0;
}

.example-text {
    font-weight: bold;
    font-size: 15px;
    text-align: center;
	margin-top: -20px;
}

/* ========================================
   PAGE 1: QUIZ INFO 
   ======================================== */

.quiz-intro-drawer {
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 0;
	margin: 0 -20px;
	overflow-y: auto;
	gap: 10px;
}

.preamble {
	margin-top: 0px;
	font-size: 15px;
	line-height: 1.467em;
	margin: 0;
	text-align: center;
}

.quiz-intro-header {
	width: 100%;
	background: #F9F7F3;
	display: flex;
	flex-direction: column;
	position: relative;
}

.quiz-intro-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
	padding: 22px 40px 0;
	z-index: 1;
	/* reduce negative lift to avoid heading being clipped */
	margin-top: -12px;
}

.quiz-intro-title {
	font-weight: bold;
	font-size: 22px;
	line-height: 1.167em;
	text-align: center;
	color: var(--background-color-primary);
	margin: 0;
}

.quiz-intro-subtitle {
	font-weight: bold;
	font-size: 15px;
	line-height: 1.25em;
	text-align: center;
	color: var(--background-color-primary);
	margin: 6px 0 0 0;
	max-width: 257px;
}

.quiz-intro-image {
	margin: 30px auto 40px;
	border-radius: 10px;
	overflow: hidden;
	background: #5A506E;
	flex-shrink: 0;
	width: 100%;
	max-width: 335px;
	height: 180px;
}

.quiz-intro-image img {
	width: 100%;
	height: 100%;
	display: block;
	object-fit: cover;
}

.quiz-intro-footer {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;
	/* add extra bottom padding to ensure buttons aren't too close to device bottom */
	padding: 20px 20px calc(40px + env(safe-area-inset-bottom) + 20px);
	flex: 1;
	background: #FFFFFF;
	/* gap: 16px; */
}

.quiz-intro-text {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	width: 100%;
	max-width: 335px;
	margin-top: 0;
	padding-top: 20px;
	padding: 20px 20px 0;
}

.quiz-intro-actions {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
	width: 100%;
}

/* ========================================
   PAGE 2: QUIZ QUESTION 
   ======================================== */

.quiz-question-drawer {
	display: flex;
	flex-direction: column;
	height: 100%;
	padding: 0;
	margin: 0 -20px;
	overflow-y: auto;
}

.quiz-question-header {
	width: 100%;
	height: 400px;
	background: #F9F7F3;
	padding: 31px 40px 40px;
	display: flex;
	flex-direction: column;
	gap: 20px;
	justify-content: space-between;
	flex-shrink: 0;
}

.quiz-day-indicator {
	display: flex;
	justify-content: center;
	align-items: center;
}

.day-badge {
	width: 56px;
	height: 56px;
	background: #FCE5D4;
	border: 1px solid white;
	border-radius: 8px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	overflow: hidden;
}

.day-badge .day-number {
	font-weight: 500;
	font-size: 21px;
	line-height: 1.333em;
	text-align: center;
	color: #FFFFFF;
	position: relative;
	z-index: 2;
}

.day-badge .peel-corner {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	object-fit: cover;
	object-position: center;
}

.quiz-question-content {
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
	overflow-y: auto;
	min-height: 0;
}

.question-text {
	font-weight: 500;
	font-size: 22px;
	line-height: 1.167em;
	text-align: center;
	color: var(--background-color-primary);
	margin: 0;
	max-width: 295px;
}

.quiz-question-footer {
	flex: 1;
	background: #FFFFFF;
	/* add extra bottom padding to keep action buttons clear of device edges */
	padding: 20px 20px calc(32px + env(safe-area-inset-bottom) + 20px);
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	overflow-y: auto;
	min-height: 0;
}

.answer-options {
	display: flex;
	flex-direction: column;
	gap: 6px;
	transition: gap 0.3s ease;
	margin-bottom: -10px;
}

.quiz-question-footer.has-selection .answer-options {
	gap: 4px;
}

.answer-option {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	min-height: 63px;
	background: #F1F0F3;
	border-radius: 8px;
	cursor: pointer;
	transition: min-height 0.3s ease, padding 0.3s ease, background 0.2s ease;
}

.quiz-question-footer.has-selection .answer-option:not(.selected) {
	padding: 6px 16px;
	min-height: 38px;
}

.answer-option.selected {
	background: #F1F0F3;
}

.answer-option.selected .answer-letter {
	background: #C0AED4 !important;
	color: #FFFFFF !important;
}

.answer-letter {
	width: 32px;
	height: 32px;
	background: #FFFFFF;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-weight: 500;
	font-size: 15px;
	color: var(--background-color-primary);
	flex-shrink: 0;
}

.answer-text {
	flex: 1;
	font-weight: 500;
	font-size: 15px;
	line-height: 1.467em;
	color: var(--background-color-primary);
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	line-clamp: 3;
	overflow: hidden;
	text-overflow: ellipsis;
	max-height: 66px;
}

.answer-text-selected {
	-webkit-line-clamp: 1;
	line-clamp: 1;
	max-height: 22px;
	white-space: nowrap;
	display: block;
}

.answer-options.small-text .answer-text {
	font-size: 12px;
}

.answer-options.small-text .answer-letter {
	font-size: 12px;
}

.answer-option.selected .answer-text {
	font-weight: bold;
}

.answer-arrow {
	width: 16px;
	height: 10px;
	margin-left: auto;
	flex-shrink: 0;
	cursor: pointer;
	transition: opacity 0.2s ease;
}

.answer-arrow:active {
	opacity: 0.7;
}

.answer-option svg {
	color: var(--background-color-primary);
	flex-shrink: 0;
}

/* ========================================
   PAGE 3: ANSWER RESULT/LOADING 
   ======================================== */

/* Loading state */
.quiz-result-loading {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100%;
	padding: 60px 20px;
	margin: 0 -20px;
	gap: 20px;
}

.loading-spinner {
	width: 50px;
	height: 50px;
	border: 4px solid #E8E4EC;
	border-top: 4px solid var(--background-color-primary);
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

@keyframes spin {
	0% { transform: rotate(0deg); }
	100% { transform: rotate(360deg); }
}

.loading-text {
	font-size: 16px;
	color: var(--background-color-primary);
	font-weight: 500;
	margin: 0;
}

/* Result state */
.quiz-result-drawer {
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 100%;
	padding: 60px 20px 20px;
	margin: 0 -20px;
	overflow-y: auto;
	gap: 30px;
}

.quiz-result-header {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	width: 100%;
}

.result-status {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 10px;
}

.result-title {
	font-weight: 500;
	font-size: 18px;
	line-height: 1.333em;
	text-align: center;
	color: var(--background-color-primary);
	margin: 0;
}

.result-stat {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 4px;
	padding: 2px 8px;
	height: 24px;
	background: rgba(65, 54, 85, 0.5);
	border-radius: 999px;
}

.result-stat span {
	font-weight: 400;
	font-size: 11px;
	line-height: 1.091em;
	text-align: center;
	color: #F9F7F3;
}

.quiz-result-content {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 40px;
	width: 100%;
	max-width: 335px;
	flex: 1;
}

.correct-answer-card {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 12px;
	padding: 20px 10px;
	width: 100%;
	height: 63px;
	background: #F1F0F3;
	border-radius: 8px;
	box-shadow: 0px 4px 8px 0px rgba(51, 40, 71, 0.05);
	box-sizing: border-box;
}

.answer-indicator {
	width: 24px;
	height: 24px;
	flex-shrink: 0;
}

.answer-letter-result {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	background: #FFFFFF;
	border-radius: 50%;
	font-weight: 500;
	font-size: 13px;
	line-height: 1.231em;
	text-align: center;
	color: var(--background-color-primary);
}

.answer-text-result {
	flex: 1;
	font-weight: 500;
	font-size: 15px;
	line-height: 1.467em;
	text-align: left;
	color: var(--background-color-primary);
}

.explanation-text {
	width: 100%;
	font-weight: 400;
	font-size: 15px;
	line-height: 1.467em;
	text-align: center;
	color: var(--background-color-primary);
	margin: 0;
	margin-top: -20px;
}

.quiz-result-footer {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: flex-end;
	gap: 16px;
	width: 100%;
	margin-top: -60px;
	/* ensure footer has extra space on devices with low safe-area support */
	padding-bottom: calc(env(safe-area-inset-bottom) + 20px);
}

.chat-button-wrapper {
	margin-top: 30px;
	margin-bottom: -15px;
}

/* ========================================
   PAGE 4: STATS
   ======================================== */

.quiz-stats-drawer {
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 100%;
	padding: 20px 20px 0;
	margin: 0 -20px;
	gap: 12px;
	overflow-y: auto;
	position: relative;
	margin-top: -40px;
}

.stats-day-badge {
	width: 128px;
	height: 128px;
	min-width: 128px;
	min-height: 128px;
	background-size: cover;
	background-position: center;
	border: 1px solid #BFB6CD;
	border-radius: 23px;
	display: flex;
	align-items: center;
	justify-content: center;
	position: relative;
	overflow: hidden;
	flex-shrink: 0;
	transition: transform 0.2s ease, opacity 0.2s ease;
	z-index: 1;
}

.stats-day-badge.clickable {
	cursor: pointer;
}

.stats-day-badge.clickable:active {
	transform: scale(0.98);
}

.stats-day-number {
	font-weight: bold;
	font-size: 48px;
	line-height: 1.333em;
	text-align: center;
	color: #F9F7F3;
	position: relative;
	z-index: 2;
}

.peel-corner-stats {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	object-fit: cover;
	object-position: center;
    height: 128px;
    width: 128px;
	opacity: 1;
	transform: scale(1);
	transition: opacity 1.2s ease-out;
}

.stats-day-badge.peel-active .peel-corner-stats {
	opacity: 0;
}

.stat-card-group {
	display: flex;
	gap: 8px;
}

.stat-card-group .stat-card {
	flex: 1;
	width: 0;
}

.stats-cards {
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
	max-width: 335px;
	position: relative;
	z-index: 1;
}

.stat-card {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
	padding: 12px;
	width: 100%;
	min-height: 78px;
	background: #FFFFFF;
	border: 1px solid rgba(65, 54, 85, 0.2);
	border-radius: 8px;
	box-sizing: border-box;
}

.stat-card.has-value {
	background: var(--background-color-primary);
	border: 1px solid var(--background-color-primary);
}

.stats-cards > .stat-card:last-child.has-value {
	background: #E1C6C8;
	border: 1px solid #E1C6C8;
}

.stats-cards > .stat-card:last-child.has-value .stat-title,
.stats-cards > .stat-card:last-child.has-value .stat-value {
	color: var(--background-color-primary);
	margin-right: -5px;
}

.stat-card.has-value .stat-title,
.stat-card.has-value .stat-value,
.stat-card.has-value .streak-current,
.stat-card.has-value .streak-total {
	color: #F9F7F3;
}

.stat-card-streak {
	flex-direction: column;
	align-items: flex-start;
	padding: 12px 12px 10px;
}

.stat-header {
	display: flex;
	flex-direction: column;
}

.stat-title {
	font-weight: 400;
	font-size: 12px;
	line-height: 1.333em;
	text-align: left;
	color: var(--background-color-primary);
	width: 84px;
}

.stat-number {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	min-width: 50px;
	height: 22px;
}

.stat-value {
	font-weight: bold;
	font-size: 20px;
	line-height: 1.2em;
	text-align: right;
	color: var(--background-color-primary);
	width: 100%;
	padding-right: 9px;
}

.stat-header-streak {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	width: 100%;
}

.streak-count {
	display: flex;
	flex-direction: row;
	align-items: flex-end;
	gap: 3px;
}

.streak-current {
	font-weight: 500;
	font-size: 20px;
	line-height: 1.2em;
	text-align: right;
	color: var(--background-color-primary);
	min-width: 30px;
}

.streak-total {
	font-weight: 400;
	font-size: 12px;
	line-height: 1.75em;
	color: var(--background-color-primary);
	opacity: 0.5;
}

.streak-milestones {
	display: flex;
	flex-direction: row;
	align-items: flex-start;
	width: 100%;
	padding: 8px 12px 24px;
	background: rgba(90, 80, 110, 0.05);
	border-radius: 5px;
	margin-top: 8px;
	justify-content: space-evenly;
}

.stat-card.has-value .streak-milestones {
	background: #5A506E;
}

.streak-milestones-empty {
	background: #FFFFFF !important;
}

.stat-card .streak-milestones-empty .milestone-circle {
	background: var(--background-color-primary) !important;
	opacity: 0.22 !important;
}

.stat-card .streak-milestones-empty .milestone-dot-small {
	background: var(--background-color-primary) !important;
	opacity: 0.22 !important;
}

.milestone-marker {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2px;
	min-width: 10px;
	position: relative;
}

.milestone-flame {
	position: absolute;
	bottom: 5px;
	left: 50%;
	transform: translateX(-50%);
	width: 40px;
	height: 40px;
	z-index: 1;
	animation: flame-appear 0.5s ease-out;
}

.milestone-flame.flame-disappear {
	animation: flame-disappear 0.5s ease-out forwards;
}

@keyframes flame-appear {
	from {
		transform: translateX(-50%) scale(0);
		opacity: 0;
	}
	to {
		transform: translateX(-50%) scale(1);
		opacity: 1;
	}
}

@keyframes flame-disappear {
	from {
		transform: translateX(-50%) scale(1);
		opacity: 1;
	}
	to {
		transform: translateX(-50%) scale(0);
		opacity: 0;
	}
}

.milestone-dot-wrapper {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 10px;
	margin-top: 2px;
}

.milestone-circle {
	width: 10px;
	height: 10px;
	background: var(--background-color-primary);
	border-radius: 50%;
	opacity: 1;
}

.stat-card.has-value .milestone-circle {
	background: #F9F7F3;
	opacity: 1;
}

.stat-card.has-value .dot-fill-animate.milestone-circle {
	animation: dot-color-change 0.3s ease-out forwards;
	background: #F9F7F3;
	opacity: 0.2;
}

.milestone-empty-large {
	width: 10px;
	height: 10px;
	background: var(--background-color-primary)	;
	border-radius: 50%;
	opacity: 0.1;
}

.stat-card.has-value .milestone-empty-large {
	background: #F9F7F3;
	opacity: 0.2;
}

.milestone-dot-small {
	width: 6px;
	height: 6px;
	background: var(--background-color-primary);
	border-radius: 50%;
	opacity: 1;
}

.stat-card.has-value .milestone-dot-small {
	background: #F9F7F3;
	opacity: 1;
}

.stat-card.has-value .dot-fill-animate.milestone-dot-small {
	animation: dot-color-change 0.3s ease-out forwards;
	background: #F9F7F3;
	opacity: 0.2;
}

@keyframes dot-color-change {
	from {
		background: #F9F7F3;
		opacity: 0.2;
	}
	to {
		background: #F9F7F3;
		opacity: 1;
	}
}

.milestone-dot-small-empty {
	width: 6px;
	height: 6px;
	background: var(--background-color-primary);
	border-radius: 50%;
	opacity: 0.1;
}

.stat-card.has-value .milestone-dot-small-empty {
	background: #F9F7F3;
	opacity: 0.2;
}

.milestone-label {
	font-size: 13px;
	line-height: 1em;
	text-align: center;
	color: var(--background-color-primary);
	opacity: 0.5;
	white-space: nowrap;
	position: absolute;
	top: 14px;
	left: 50%;
	transform: translateX(-50%);
}

.stat-card.has-value .milestone-label {
	color: #F9F7F3;
	opacity: 0.5;
}

.milestone-label-active {
	color: var(--background-color-primary) !important;
	opacity: 1 !important;
}

.stat-card.has-value .milestone-label-active {
	color: #F9F7F3 !important;
	opacity: 1 !important;
}

.stats-footer {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	flex: 1;
	gap: 4px;
	padding-bottom: calc(20px + env(safe-area-inset-bottom));
	position: relative;
	z-index: 1;
}

.stats-message {
	font-weight: 400;
	font-size: 15px;
	line-height: 1.467em;
	text-align: center;
	color: var(--background-color-primary);
	margin: 0 0 40px 0;
	width: 100%;
	max-width: 335px;
}

.quiz-notifications {
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-top: 16px;
	margin-bottom: -16px;
}

.quiz-notifications-title {
	font-weight: bold;
	font-size: 15px;
	line-height: 1.467em;
	text-align: center;
	color: var(--background-color-primary);
	margin: 0;
}

.quiz-notifications-toggle {
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	max-width: 194px;
}

.toggle-label {
	font-family: Gotham;
	font-weight: 400;
	font-size: 15px;
	line-height: 1.467em;
	color: var(--background-color-primary);
}

.toggle-control {
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 8px;
	margin-left: 8px;
}

.toggle-text {
	font-weight: normal !important;
	font-size: 15px;
	line-height: 1.467em;
	color: var(--background-color-primary) !important;
	background: none !important;
	background-color: transparent !important;
}

.toggle-text.active {
	font-weight: bold !important;
	background: none !important;
	background-color: transparent !important;
}

.toggle-switch {
	width: 26px;
	height: 15px;
	background: linear-gradient(180deg, rgba(102, 102, 102, 1) 0%, rgba(102, 102, 102, 0) 68%), grey;
	border-radius: 11px;
	position: relative;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: inset 0px 0.5px 2.5px 0px rgba(102, 102, 102, 0.75);
}

.toggle-switch.toggle-on {
	background: var(--background-color-alt3);
}

.toggle-switch.toggle-on .toggle-knob {
	transform: translateX(11px);
}

.toggle-knob {
	position: absolute;
	left: 1px;
	top: 1px;
	width: 13px;
	height: 13px;
	background: #FFFFFF;
	border: 0.5px solid rgba(0, 0, 0, 0.02);
	border-radius: 100px;
	transition: transform 0.3s ease;
	box-shadow: 0px 0.2px 0.25px 0px rgba(0, 0, 0, 0.12);
}

.bonus-animation {
	position: absolute;
	font-size: 48px;
	font-weight: bold;
	color: white;
	animation: bonus-fade 1s ease-out;
	z-index: 10;
}

.stats-cards > .stat-card:last-child .bonus-animation {
	color: var(--background-color-primary);
}

@keyframes bonus-fade {
	from {
		transform: scale(1);
		opacity: 1;
	}
	to {
		transform: scale(0.5);
		opacity: 0;
	}
}

/* ========================================
   MEDIA QUERIES
   ======================================== */

/* Page 2: Quiz Question adjustments for medium-small screens */
@media (max-height: 750px) {
	.quiz-question-header {
		height: 180px;
		padding: 20px 40px 30px;
		gap: 15px;
	}

	.question-text {
		font-size: 18px;
		line-height: 1.2em;
	}

	.day-badge {
		width: 44px;
		height: 44px;
	}

	.day-badge .day-number {
		font-size: 16px;
	}
}

/* Page 2: Quiz Question adjustments for small screens */
@media (max-height: 820px) {
	.quiz-question-header {
		height: 140px;
		padding: 14px 28px 18px;
		gap: 8px;
	}

	.question-text {
		font-size: 17px;
		line-height: 1.15em;
		max-width: 260px;
	}

	.day-badge {
		width: 40px;
		height: 40px;
	}

	.day-badge .day-number {
		font-size: 14px;
	}
}

/* Pages 1, 3, 4: Adjustments for very small screens */
@media (max-height: 700px) {
	/* Page 4: Hide stats badge to save space */
	.stats-day-badge {
		display: none;
	}

	/* Page 1: Make quiz intro more compact */
	.quiz-intro-content {
		padding: 10px 40px 0;
		margin-top: -15px;
		gap: 4px;
	}

	.quiz-intro-title {
		font-size: 20px;
	}

	.quiz-intro-subtitle {
		font-size: 14px;
	}

	.quiz-intro-image {
		margin: 20px auto 30px;
		width: 280px;
		height: 177px;
	}

	.quiz-intro-footer {
		padding: 10px 20px calc(20px + env(safe-area-inset-bottom) + 12px);
		gap: 8px;
	}

	.quiz-intro-text {
		padding-top: 10px;
		gap: 4px;
	}

	.quiz-intro-actions {
		gap: 8px;
	}

	/* Page 3: Make result page more compact */
	.quiz-result-drawer {
		padding: 30px 20px 20px;
		gap: 20px;
	}

	.quiz-result-content {
		gap: 20px;
	}

	.explanation-text {
		margin-top: -10px;
		font-size: 14px;
	}

	.quiz-result-footer {
		margin-top: -30px;
		gap: 8px;
		padding-bottom: calc(env(safe-area-inset-bottom) + 12px);
	}

	.chat-button-wrapper {
		margin-top: 15px;
		margin-bottom: -10px;
	}
}
</style>
